import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EffectRef,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { DYNAMIC_ELEMENTS_MAP_EXTERNAL, MultiStepFormComponent, StepSubmitHandler } from '@libs/form/components';
import { type OrderHistoryRecordModel, SRM_TICKET_SUGGESTION_COMPONENT_ID } from '@libs/services/models';
import { GroupValueType, MultiStepFormItemModel, StepItemModel } from '@libs/form/models';
import { SrmTrackingLocation } from '@libs/shared/models';
import { SrmNavigationService } from '../srm-navigation.service';
import { SrmTicketSuggestionComponent } from '../srm-ticket-suggestion';
import { SrmCreateFormService } from './srm-create-form.service';

@Component({
  selector: 'ui-srm-create-form',
  standalone: true,
  templateUrl: './srm-create-form.component.html',
  styleUrls: ['./srm-create-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MultiStepFormComponent,
  ],
  providers: [
    SrmCreateFormService,
    {
      provide: DYNAMIC_ELEMENTS_MAP_EXTERNAL,
      useValue: {
        [SRM_TICKET_SUGGESTION_COMPONENT_ID]: SrmTicketSuggestionComponent,
      },
    },
  ],
})
export class SrmCreateFormComponent {
  public readonly order: InputSignal<OrderHistoryRecordModel | undefined> = input();
  public readonly location: InputSignal<SrmTrackingLocation | undefined> = input();

  public readonly success: OutputEmitterRef<void> = output<void>();
  public readonly formClosed: OutputEmitterRef<void> = output<void>();

  public readonly form: WritableSignal<MultiStepFormItemModel | undefined> = signal(undefined);

  protected readonly formControl: FormGroup = new FormGroup({});

  private readonly formService: SrmCreateFormService = inject(SrmCreateFormService);
  private readonly router: Router | null = inject(Router, { optional: true });
  private readonly navigation: SrmNavigationService = inject(SrmNavigationService);

  private readonly formEffectRef: EffectRef = effect(() => {
    void this.initForm(this.order(), this.location());
  });

  public readonly stepSubmitHandler: StepSubmitHandler = async (
    currentStep: StepItemModel,
    fullFormValue: GroupValueType,
    submitterName?: string,
  ): Promise<StepItemModel | undefined> => {
    if (currentStep.name === 'success') {
      return void this.handleSuccess(submitterName);
    }

    return this.formService.submitStep(currentStep, fullFormValue);
  };

  public onStepBack(step: StepItemModel): void {
    this.formService.trackBack(step.name);
  }

  private handleSuccess(submitterName?: string): void {
    if (!submitterName) {
      return;
    }

    if (submitterName === 'restart') {
      void this.initForm(this.order(), this.location());

      return;
    }

    if (submitterName === 'done') {
      this.formService.trackDone();
      this.success.emit();
      this.formClosed.emit();
      void this.router?.navigate([this.navigation.overviewPath()]);

      return;
    }
  }

  private async initForm(order?: OrderHistoryRecordModel, location?: SrmTrackingLocation): Promise<void> {
    return this.formService.initForm(order, location, this.formControl)
      .then(form => void this.form.set(form))
      .catch(() => void this.form.set(undefined));
  }
}
